import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, CardBody } from 'reactstrap';

import { isBiller, isPatient } from 'utils/userTypeHelper';
import withLoader from 'components/organisms/withLoader';
import CareAgents from 'components/molecules/careAgents/careAgents';
import useUser from 'utils/useUser';
import PatientStatusLogs from 'components/templates/admin/patient/components/statusLogs';
import ClinicRunBookLogs from 'components/templates/admin/patient/components/clinicRunBookLogs';
import PatientCcmStatusLogs from 'components/templates/admin/patient/components/CcmStatusLogs';
import PatientHoldingStatusLogs from 'components/templates/admin/patient/components/holdingStatusLogs';
import PatientInfo from '../patientInfo';
import PatientDevices from '../patientDevices';
import PatientThresholds from '../patientThresholds';
import PatientDoctorsInfo from '../patientDoctorsInfo';
import PatientPriorAuthorizations from '../patientPriorAuthorizations';
import PatientInsurances from '../patientInsurances';
import GeneralComments from '../generalComments';
import Messages from '../messages';
import EditProfileModal from '../patientInfo/modal';
import EditThresholdsModal from '../patientThresholds/modal';
import PatientDxEmr from '../patientDxEmr';
import ManualInteractionTimes from '../manualInteractiveTimes';

const UserProfile = ({
  patientGuid,
  patientDetailsLoading,
  patient,
  showMessages,
}) => {
  const { userType, roleNames } = useUser();
  const loading = useSelector((state) => state.loading.effects.patient.submit);
  const loadingThresholds = useSelector(
    (state) => state.loading.effects.patient.submitThresholds,
  );
  const {
    patient: { submit: submitProfile, submitThresholds },
  } = useDispatch();

  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const [thresholdsPopupOpen, setThresholdsPopupOpen] = useState(false);

  return (
    <div className="user-profile-block">
      <Card className="card-border">
        <CardBody>
          <h3>Patient Profile</h3>
          <div className="user-profile-block-body">
            <Row>
              <Col md="12" xl="8">
                {withLoader(
                  patientDetailsLoading,
                  <Row>
                    <Col xs="12" md="6">
                      <PatientInfo
                        profile={patient.profile}
                        handleEditClick={() => setProfilePopupOpen(true)}
                      />
                      <PatientDxEmr
                        diagnosis={patient.diagnosis}
                        emr={patient.emr}
                      />
                      <PatientPriorAuthorizations
                        priorAuthorizations={patient.priorAuthorizations}
                      />
                      <PatientInsurances insurances={patient.insurances} />
                    </Col>
                    <Col xs="12" md="6">
                      <PatientThresholds
                        thresholds={patient.thresholds}
                        handleEditClick={() => setThresholdsPopupOpen(true)}
                      />
                      <PatientDevices devices={patient.devices} />
                      <PatientDoctorsInfo
                        doctor={patient.doctor}
                        clinic={patient.clinic}
                      />
                      <ClinicRunBookLogs
                        clinicGuid={patient?.clinic?.clinicGuid}
                      />
                      <CareAgents
                        agents={patient.careAgents}
                        label="Care Agents"
                        displayProfType
                      />
                    </Col>
                  </Row>,
                )}
              </Col>
              <Col xs="12" md="6" xl="4">
                {!isPatient(userType) && (
                  <GeneralComments patientGuid={patientGuid} isRpmPanel />
                )}
                <ManualInteractionTimes patientGuid={patientGuid} />
                {showMessages && <Messages patientGuid={patientGuid} />}
                <PatientStatusLogs patientGuid={patientGuid} />
                <PatientCcmStatusLogs patientGuid={patientGuid} />
                {!isBiller(roleNames) && (
                  <PatientHoldingStatusLogs patientGuid={patientGuid} />
                )}
              </Col>
            </Row>
          </div>
        </CardBody>
      </Card>
      <EditProfileModal
        isOpen={profilePopupOpen}
        toggle={setProfilePopupOpen}
        patientGuid={patientGuid}
        loading={loading}
        submit={submitProfile}
        profile={patient.profile}
        isRpmPanel
      />
      <EditThresholdsModal
        isOpen={thresholdsPopupOpen}
        toggle={setThresholdsPopupOpen}
        patientGuid={patientGuid}
        loading={loadingThresholds}
        submit={submitThresholds}
        thresholds={patient.thresholds}
      />
    </div>
  );
};

UserProfile.propTypes = {
  patientGuid: PropTypes.string.isRequired,
  patientDetailsLoading: PropTypes.bool.isRequired,
  patient: PropTypes.shape(),
  showMessages: PropTypes.bool,
};

export default UserProfile;
